import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CaretLeft, Sparkle, PlayCircle, PencilSimple, FloppyDisk, Plus, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiscoverPage() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ liquid_glass_enabled: false, detailing_i_plote_enabled: false });

  const canEdit = isSuperAdmin || user?.role === 'location_manager';

  const fetchAll = async () => {
    try {
      const [v, s] = await Promise.all([
        axios.get(`${API}/zbulo/videos`),
        axios.get(`${API}/settings/public`),
      ]);
      setVideos(v.data || []);
      setSettings(s.data || {});
    } catch (err) { console.warn('[Zbulo] load failed', err?.response?.status || err?.message); }
  };

  useEffect(() => {
    fetchAll();
    localStorage.setItem('autospa5_zbulo_last_visit', new Date().toISOString());
  }, []);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(videos))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft([]); };

  const updateDraft = (idx, field, value) => {
    const next = [...draft]; next[idx] = { ...next[idx], [field]: value };
    setDraft(next);
  };

  // Extract YT id from any URL form
  const extractYtId = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    return match ? match[1] : url;
  };

  const updateDraftUrl = (idx, urlValue) => {
    const yt_id = extractYtId(urlValue);
    updateDraft(idx, 'youtube_id', yt_id);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/zbulo/videos`, { videos: draft }, { withCredentials: true });
      toast.success('U ruajt');
      setVideos(draft);
      setEditing(false);
    } catch (e) { toast.error('Gabim'); }
    setSaving(false);
  };

  const addVideo = () => {
    setDraft([...draft, {
      id: `video_${Date.now()}`,
      title: 'Video e re',
      subtitle: '',
      youtube_id: 'dQw4w9WgXcQ',
      category: 'portal',
    }]);
  };
  const removeVideo = (idx) => setDraft(draft.filter((_, i) => i !== idx));

  // Filter visible videos based on toggles (H2)
  const visibleVideos = videos.filter((v) => {
    if (v.category === 'liquid_glass' && !settings.detailing_enabled) return false;
    if (v.category === 'detailing_i_plote' && !settings.detailing_i_plote_enabled) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24" data-testid="zbulo-page">
      <div className="px-4 pt-8 pb-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')}
                className="h-9 w-9 rounded-xl text-white/70 hover:bg-white/5" data-testid="zbulo-back">
          <CaretLeft size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold tracking-tight font-['Outfit'] flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-[#FFD700]" />
            Zbulo
          </h1>
          <p className="text-xs text-white/45">Video edukative AutoSpa5</p>
        </div>
        {canEdit && !editing && (
          <Button size="sm" onClick={startEdit} data-testid="zbulo-edit-btn"
                  className="h-9 px-3 rounded-lg bg-[#0ABAB5] text-black hover:bg-[#0ABAB5]/90 text-xs gap-1">
            <PencilSimple size={11} weight="fill" /> Modifiko
          </Button>
        )}
      </div>

      {/* EDIT MODE */}
      {editing && (
        <div className="px-4 space-y-3" data-testid="zbulo-editor">
          {draft.map((v, i) => (
            <div key={i} className="p-3 rounded-2xl bg-[#0a0a0a] border border-[#0ABAB5]/30 space-y-2"
                 data-testid={`zbulo-edit-row-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Video {i + 1}</p>
                <button onClick={() => removeVideo(i)} className="text-red-400">
                  <Trash size={14} weight="fill" />
                </button>
              </div>
              <Input placeholder="URL YouTube (es. https://youtu.be/XXXXXXXXXXX)"
                     defaultValue={v.youtube_id ? `https://youtu.be/${v.youtube_id}` : ''}
                     onBlur={(e) => updateDraftUrl(i, e.target.value)}
                     className="h-9 bg-[#050505] border-white/10 rounded-xl text-xs font-mono"
                     data-testid={`zbulo-edit-url-${i}`} />
              <Input placeholder="Titulli" value={v.title || ''}
                     onChange={(e) => updateDraft(i, 'title', e.target.value)}
                     className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm"
                     data-testid={`zbulo-edit-title-${i}`} />
              <Input placeholder="Përshkrim i shkurtër" value={v.subtitle || ''}
                     onChange={(e) => updateDraft(i, 'subtitle', e.target.value)}
                     className="h-9 bg-[#050505] border-white/10 rounded-xl text-sm"
                     data-testid={`zbulo-edit-subtitle-${i}`} />
              <select value={v.category || 'portal'}
                      onChange={(e) => updateDraft(i, 'category', e.target.value)}
                      className="w-full h-9 bg-[#050505] border border-white/10 rounded-xl text-sm px-2 text-white"
                      data-testid={`zbulo-edit-category-${i}`}>
                <option value="portal">Portal lavaggio (sempre visibile)</option>
                <option value="liquid_glass">Liquid Glass (visibile se toggle ON)</option>
                <option value="detailing_i_plote">Detailing i Plotë (visibile se toggle ON)</option>
              </select>
            </div>
          ))}
          <Button onClick={addVideo} variant="outline"
                  className="w-full h-10 rounded-xl border-dashed border-white/20 text-white/60 text-xs gap-1.5">
            <Plus size={12} weight="bold" /> Shto video
          </Button>
          <div className="flex gap-2">
            <Button onClick={cancelEdit} variant="outline"
                    className="flex-1 h-10 rounded-xl border-white/15 text-white/70 text-xs">Anulo</Button>
            <Button onClick={saveAll} disabled={saving} data-testid="zbulo-save-btn"
                    className="flex-1 h-10 rounded-xl bg-[#FF5A00] hover:bg-[#FF5A00]/90 text-white text-xs gap-1">
              <FloppyDisk size={12} weight="fill" />{saving ? '…' : 'Ruaj të gjitha'}
            </Button>
          </div>
        </div>
      )}

      {/* VIEW MODE */}
      {!editing && (
        <div className="px-4 space-y-4">
          {visibleVideos.length === 0 ? (
            <p className="text-center text-white/40 text-sm py-12">Nuk ka video.</p>
          ) : visibleVideos.map((v, i) => (
            <motion.div key={v.id || i}
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="rounded-2xl bg-[#0a0a0a] border border-white/5 overflow-hidden"
                        data-testid={`zbulo-card-${v.id || i}`}>
              <div className="aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${v.youtube_id}`}
                  title={v.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <PlayCircle size={14} weight="fill" className="text-[#FF5A00]" />
                  <p className="text-sm font-semibold text-white">{v.title}</p>
                </div>
                {v.subtitle && <p className="text-xs text-white/55 mb-2">{v.subtitle}</p>}
                {/* Iter24 H2 — CTA Rezervo per Liquid Glass / Detailing categories */}
                {v.category === 'liquid_glass' && (
                  <Button onClick={() => navigate('/liquid-glass')}
                          className="w-full h-9 rounded-xl bg-[#FF5A00] text-white text-xs"
                          data-testid={`zbulo-cta-${v.category}`}>
                    Rezervo Liquid Glass
                  </Button>
                )}
                {v.category === 'detailing_i_plote' && (
                  <Button onClick={() => navigate('/detailing-i-plote')}
                          className="w-full h-9 rounded-xl bg-[#A78BFA] text-white text-xs"
                          data-testid={`zbulo-cta-${v.category}`}>
                    Rezervo Detailing
                  </Button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
